import React, { createContext, useContext, useMemo, useState, useEffect } from "react";
import { MONO, SANS } from "./brand";

/* ── the annotation layer ───────────────────────────────────────
   A page wraps its real DOM blocks in <Block id="...">. Turn the
   layer on and every registered block gets a numbered pin. The pin
   opens the split test record for that exact piece of the page. */

const PIN = "#7C4DFF";
const PANEL_BG = "#0C1016";

export const STATES = {
  candidate: { c: "#8894A8", t: "candidate", d: "Written, queued, not live yet." },
  directional: { c: "#FFC46B", t: "directional", d: "Live, moving the metric, not enough traffic to call it." },
  validated: { c: "#5FE3A1", t: "validated", d: "Called once at significance on a clean split." },
  repeatable: { c: "#4C9AFF", t: "repeatable", d: "Held up on a second audience or a second month." },
  fatigued: { c: "#FF6B6B", t: "fatigued", d: "Won, then decayed. Retired or rebuilt." },
};

const Ctx = createContext(null);
export const useAnno = () => useContext(Ctx);

export function AnnotationProvider({ pins, children }) {
  const [on, setOn] = useState(
    () => typeof window !== "undefined" && new URLSearchParams(window.location.search).get("tests") === "1"
  );
  const [active, setActive] = useState(null);
  const index = useMemo(
    () => Object.fromEntries(pins.map((p, i) => [p.id, { ...p, n: i + 1 }])),
    [pins]
  );
  const numbered = useMemo(() => pins.map((p, i) => ({ ...p, n: i + 1 })), [pins]);

  useEffect(() => {
    if (!on) setActive(null);
  }, [on]);

  const open = (id) => {
    setActive(id);
    const el = document.querySelector(`[data-block="${id}"]`);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 120;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <Ctx.Provider value={{ on, setOn, active, setActive, open, index, pins: numbered }}>
      {children}
      {on && <Drawer />}
    </Ctx.Provider>
  );
}

export function Block({ id, children, className = "", pin = "tl", as: Tag = "div", style }) {
  const a = useAnno();
  const rec = a?.index?.[id];
  if (!a?.on || !rec) {
    return (
      <Tag className={className} data-block={id} style={style}>
        {children}
      </Tag>
    );
  }
  const live = a.active === id;
  const pos =
    pin === "tr"
      ? { top: -13, right: -13 }
      : pin === "bl"
      ? { bottom: -13, left: -13 }
      : { top: -13, left: -13 };
  return (
    <Tag
      className={`relative ${className}`}
      data-block={id}
      data-testid={`block-${id}`}
      style={{
        ...style,
        outline: `${live ? 2 : 1.5}px dashed ${live ? "#5FE3A1" : PIN}`,
        outlineOffset: 3,
        borderRadius: 8,
        transition: "outline-color 180ms ease",
      }}
    >
      <button
        type="button"
        data-testid={`pin-${rec.n}`}
        aria-label={`Split test ${rec.n}: ${rec.block}`}
        onClick={() => a.open(id)}
        className="absolute z-[60] grid h-[26px] w-[26px] place-items-center rounded-full text-[11px] font-semibold"
        style={{
          ...pos,
          fontFamily: MONO,
          color: "#fff",
          background: live ? "#1E9E6A" : PIN,
          boxShadow: `0 0 0 3px ${live ? "#5FE3A155" : PIN + "33"}, 0 6px 14px rgba(0,0,0,0.35)`,
          cursor: "pointer",
        }}
      >
        {rec.n}
      </button>
      {children}
    </Tag>
  );
}

/* ── toolbar, dropped by the shell ── */
export function AnnotationToggle() {
  const a = useAnno();
  if (!a) return null;
  return (
    <button
      type="button"
      data-testid="annotation-toggle"
      onClick={() => a.setOn(!a.on)}
      className="flex items-center gap-2.5 rounded-full py-2 pl-2.5 pr-4 text-[12px] font-medium transition-colors"
      style={{
        fontFamily: SANS,
        color: a.on ? "#fff" : "#C9D2E0",
        background: a.on ? PIN : "rgba(255,255,255,0.08)",
        border: `1px solid ${a.on ? PIN : "rgba(255,255,255,0.16)"}`,
      }}
    >
      <span
        className="grid h-[18px] w-[18px] place-items-center rounded-full text-[10px]"
        style={{ background: a.on ? "#fff" : "rgba(255,255,255,0.16)", color: a.on ? PIN : "#C9D2E0", fontFamily: MONO }}
      >
        {a.pins.length}
      </span>
      {a.on ? "Split test layer on" : "Show the split tests"}
    </button>
  );
}

function Chip({ state }) {
  const s = STATES[state] || STATES.candidate;
  return (
    <span
      className="inline-block rounded px-2 py-[3px] text-[9px] uppercase tracking-[0.12em]"
      style={{ fontFamily: MONO, color: s.c, border: `1px solid ${s.c}55`, background: `${s.c}14` }}
    >
      {s.t}
    </span>
  );
}

function Side({ label, lines, tone }) {
  return (
    <div className="rounded-lg border p-3" style={{ borderColor: "#232A35", background: "#12161D" }}>
      <div className="mb-2 text-[9px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: tone }}>
        {label}
      </div>
      {lines.map((l, i) => (
        <div key={i} className="mb-1.5 flex gap-2 text-[12px] leading-snug" style={{ color: "#D7DEE9" }}>
          <span style={{ color: tone }}>·</span>
          <span>{l}</span>
        </div>
      ))}
    </div>
  );
}

function Drawer() {
  const a = useAnno();
  const rec = a.active ? a.index[a.active] : null;
  const s = rec ? STATES[rec.state] || STATES.candidate : null;

  return (
    <div
      data-testid="annotation-drawer"
      className="fixed bottom-0 right-0 top-0 z-[80] flex w-full max-w-[420px] flex-col border-l"
      style={{
        background: PANEL_BG,
        borderColor: "#222A36",
        fontFamily: SANS,
        boxShadow: "-24px 0 60px rgba(0,0,0,0.45)",
      }}
    >
      <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: "#222A36" }}>
        <div>
          <div className="text-[9px] uppercase tracking-[0.18em]" style={{ fontFamily: MONO, color: PIN }}>
            Split test log
          </div>
          <div className="mt-1 text-[13px]" style={{ color: "#E7ECF3" }}>
            {a.pins.length} tests on this page
          </div>
        </div>
        <button
          type="button"
          data-testid="annotation-close"
          onClick={() => a.setOn(false)}
          className="rounded px-2.5 py-1 text-[11px]"
          style={{ color: "#93A0B4", border: "1px solid #2A323E" }}
        >
          Close
        </button>
      </div>

      <div className="thin-scroll flex-1 overflow-y-auto px-5 py-4">
        {!rec && (
          <div>
            <p className="mb-4 text-[12.5px] leading-relaxed" style={{ color: "#93A0B4" }}>
              Every numbered pin on the page is a piece we have actually tested. Click one, or pick from the list. Some pins
              live on a later screen, so picking the row takes you there first.
            </p>
            {a.pins.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`drawer-item-${p.n}`}
                onClick={() => a.open(p.id)}
                className="mb-2 flex w-full items-start gap-3 rounded-lg border p-3 text-left"
                style={{ borderColor: "#232A35", background: "#11151C" }}
              >
                <span
                  className="grid h-[22px] w-[22px] shrink-0 place-items-center rounded-full text-[10px]"
                  style={{ background: PIN, color: "#fff", fontFamily: MONO }}
                >
                  {p.n}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-[12.5px]" style={{ color: "#E7ECF3" }}>
                    {p.block}
                  </span>
                  <span className="mt-1 block text-[11.5px]" style={{ color: "#7E8B9E" }}>
                    {p.piece}
                  </span>
                </span>
                <Chip state={p.state} />
              </button>
            ))}
          </div>
        )}

        {rec && (
          <div data-testid="annotation-detail">
            <button
              type="button"
              data-testid="annotation-back"
              onClick={() => a.setActive(null)}
              className="mb-4 text-[11px]"
              style={{ color: "#7E8B9E" }}
            >
              All tests
            </button>
            <div className="flex items-start gap-3">
              <span
                className="grid h-[26px] w-[26px] shrink-0 place-items-center rounded-full text-[11px]"
                style={{ background: "#1E9E6A", color: "#fff", fontFamily: MONO }}
              >
                {rec.n}
              </span>
              <div>
                <div className="text-[15px] leading-snug" style={{ color: "#F2F5F9" }}>
                  {rec.block}
                </div>
                <div className="mt-1 text-[12px]" style={{ color: "#8B97AA" }}>
                  {rec.piece}
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-2">
              <Chip state={rec.state} />
              <span className="text-[11px]" style={{ color: "#6F7C8F" }}>
                {s.d}
              </span>
            </div>

            <Row label="Hypothesis" v={rec.hypothesis} />
            <Row label="Metric it is judged on" v={rec.metric} />

            <div className="mt-4 grid gap-2.5">
              <Side label="Control" lines={rec.control} tone="#8894A8" />
              <Side label="Variant" lines={rec.variant} tone="#5FE3A1" />
            </div>

            <Row label="What happened" v={rec.result} />
            <Row label="What shipped" v={rec.shipped} />

            <div className="mt-5 flex items-center justify-between border-t pt-4" style={{ borderColor: "#232A35" }}>
              <button
                type="button"
                data-testid="annotation-prev"
                disabled={rec.n === 1}
                onClick={() => a.open(a.pins[rec.n - 2].id)}
                className="rounded px-3 py-1.5 text-[11px] disabled:opacity-30"
                style={{ color: "#A6B2C4", border: "1px solid #2A323E" }}
              >
                Previous
              </button>
              <span className="text-[10px]" style={{ fontFamily: MONO, color: "#5F6B7A" }}>
                {rec.n} of {a.pins.length}
              </span>
              <button
                type="button"
                data-testid="annotation-next"
                disabled={rec.n === a.pins.length}
                onClick={() => a.open(a.pins[rec.n].id)}
                className="rounded px-3 py-1.5 text-[11px] disabled:opacity-30"
                style={{ color: "#A6B2C4", border: "1px solid #2A323E" }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, v }) {
  return (
    <div className="mt-4">
      <div className="mb-1.5 text-[9px] uppercase tracking-[0.16em]" style={{ fontFamily: MONO, color: "#5F6B7A" }}>
        {label}
      </div>
      <p className="text-[12.5px] leading-relaxed" style={{ color: "#D7DEE9" }}>
        {v}
      </p>
    </div>
  );
}
